import type { Nullish } from '@iheartjane/types';
import uniq from 'lodash-es/uniq';

import type { FormRules } from '@jane/business-admin/types';
import type {
  ExclusionOverlap,
  FoldableKey,
} from '@jane/shared-b2b/components';
import {
  FOLDABLE_KEYS,
  foldExclusions,
  getExclusionOverlaps,
} from '@jane/shared-b2b/components';

export type ExclusionAxis = 'brands' | 'kinds' | 'lineages' | 'weights';

const AXIS_BY_KEY: Record<FoldableKey, ExclusionAxis> = {
  kinds: 'kinds',
  brand_ids: 'brands',
  brands: 'brands',
  lineages: 'lineages',
  weights: 'weights',
  weight_values: 'weights',
};

/**
 * Rewrites the first inclusion/exclusion pair so that categories, brands,
 * lineages, and weights are expressed by inclusions alone wherever that
 * matches the same products.
 */
export const foldRedundantExclusions = <T extends FormRules>(rules: T): T => {
  const [include, ...otherIncludes] = rules.includes ?? [];
  const [exclude, ...otherExcludes] = rules.excludes ?? [];
  if (!include || !exclude) return rules;

  const folded = foldExclusions(include, exclude);
  if (folded.include === include && folded.exclude === exclude) return rules;

  return {
    ...rules,
    includes: [folded.include, ...otherIncludes],
    excludes: [folded.exclude, ...otherExcludes],
  };
};

const axesWithOverlap = (
  rules: Nullish<FormRules>,
  overlap: ExclusionOverlap
): ExclusionAxis[] => {
  const overlaps = getExclusionOverlaps(
    rules?.includes?.[0],
    rules?.excludes?.[0]
  );

  return uniq(
    FOLDABLE_KEYS.filter((key) => overlaps[key] === overlap).map(
      (key) => AXIS_BY_KEY[key]
    )
  );
};

/** The axes that foldRedundantExclusions would rewrite */
export const getFoldableExclusionAxes = (
  rules: Nullish<FormRules>
): ExclusionAxis[] => axesWithOverlap(rules, 'redundant');

/**
 * The axes whose exclusions rule out every inclusion, so the rule matches no
 * products. foldRedundantExclusions leaves these alone, since emptying the
 * inclusions would widen the rule to match everything instead.
 */
export const getConflictingExclusionAxes = (
  rules: Nullish<FormRules>
): ExclusionAxis[] => axesWithOverlap(rules, 'conflicting');
